import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { CoverImage } from '@/components/CoverImage'

export default function ChromaFestivalCaseStudyPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/chroma-festival-case-study', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: "Case Study: Building Chroma Festival's Complete Digital Infrastructure",
    description:
      'How we built a modern festival website, streamlined application system, and custom admin panel to launch a new classical music festival successfully.',
    url: '/blog/chroma-festival-case-study',
    canonical: '/blog/chroma-festival-case-study',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2024-12-20',
      author: 'Garrett John Law',
      section: 'Case Studies',
      tags: ['festival website', 'application system', 'digital strategy', 'classical music'],
    },
    schema: createArticleSchema({
      headline: "Building Chroma Festival's Complete Digital Infrastructure from Scratch",
      description:
        'How we built a modern festival website, streamlined application system, and custom admin panel to launch a new classical music festival successfully.',
      datePublished: '2024-12-20',
      url: '/blog/chroma-festival-case-study',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-4xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4 text-center">
        <div className="text-primary text-sm font-semibold tracking-wide uppercase">Case Study</div>
        <h1 className="text-4xl leading-tight font-bold md:text-5xl">
          Building Chroma Festival&apos;s Complete Digital Infrastructure from Scratch
        </h1>
        <p className="text-muted-foreground mx-auto max-w-3xl text-xl">
          How we built a modern festival website, streamlined application system, and custom admin panel to launch a new
          classical music festival successfully.
        </p>
      </header>

      <CoverImage slug="chroma-festival-case-study" alt="Chroma Festival's modern website homepage" />

      {/* Key Results */}
      <div className="bg-primary/5 border-primary/20 article-section rounded-lg border p-8">
        <h2 className="mb-6 text-center text-2xl font-semibold">What We Built</h2>
        <div className="grid gap-6 text-center md:grid-cols-3">
          <div>
            <div className="text-primary text-4xl font-bold">8 weeks</div>
            <div className="text-muted-foreground mt-2 text-sm">From kickoff to launch</div>
          </div>
          <div>
            <div className="text-primary text-4xl font-bold">3 systems</div>
            <div className="text-muted-foreground mt-2 text-sm">Website, application funnel, admin panel</div>
          </div>
          <div>
            <div className="text-primary text-4xl font-bold">10+ hrs/week</div>
            <div className="text-muted-foreground mt-2 text-sm">Saved on admin work</div>
          </div>
        </div>
      </div>

      {/* About the Client */}
      <section className="article-section space-y-4">
        <h2 className="text-3xl font-bold">About Chroma International Music Festival</h2>
        <p className="text-muted-foreground leading-relaxed">
          Chroma International Music Festival is a new student-focused summer music program offering intensive chamber
          music training for young musicians. The festival brings together talented students from around the world for a
          transformative two-week experience with world-class faculty.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          As a brand-new festival launching its first season, Chroma needed a complete digital infrastructure to manage
          applications, communicate with students, and present a professional online presence—all while keeping
          administrative overhead minimal.
        </p>
      </section>

      {/* The Challenge */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">The Challenge</h2>
        <p className="text-muted-foreground leading-relaxed">
          When the festival director reached out to Fugue Lab, Chroma was starting from zero. They needed:
        </p>

        <div className="space-y-6">
          <div className="border-primary/50 border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">1. A Professional Festival Website (Fast)</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Modern, mobile-optimized design that reflects the quality of the festival&apos;s programs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Fast load times (Gen Z applicants won&apos;t wait for slow websites)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Easy-to-update content management (no developer needed for routine changes)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Timeline: 8 weeks from kickoff to launch</span>
              </li>
            </ul>
          </div>

          <div className="border-primary/50 border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">2. A Streamlined Application System</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Clear, easy-to-complete application process for students</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Integrated payment processing (no manual invoicing)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Save-and-resume functionality (students shouldn&apos;t lose progress)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>File upload for repertoire lists and recommendation letters</span>
              </li>
            </ul>
          </div>

          <div className="border-primary/50 border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">3. An Admin Panel to Manage Everything</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Central dashboard to view all applicants at a glance (no spreadsheet chaos)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Track application status: incomplete, submitted, accepted, enrolled</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Filter and search applicants by instrument, age, location</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Export applicant data for review and decision-making</span>
              </li>
            </ul>
          </div>

          <div className="border-primary/50 border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">4. Minimal Administrative Overhead</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>
                  The festival director needed to focus on programming and faculty recruitment—not managing tech
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Automated email confirmations and reminders (no manual follow-ups)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&rarr;</span>
                <span>Self-service CMS for content updates</span>
              </li>
            </ul>
          </div>
        </div>

        <blockquote className="border-primary text-muted-foreground bg-muted/30 rounded-r border-l-4 py-4 pl-6 text-lg italic">
          &quot;We needed everything—website, application system, payment processing—and we needed it fast. I
          didn&apos;t want to cobble together five different tools and pray they worked together. I wanted one cohesive
          system built specifically for a music festival.&quot;
          <footer className="mt-2 text-sm not-italic">— Festival Director, Chroma International Music Festival</footer>
        </blockquote>
      </section>

      {/* The Solution */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">The Solution</h2>
        <p className="text-muted-foreground leading-relaxed">
          Fugue Lab built Chroma&apos;s complete digital infrastructure from scratch in 8 weeks. Here&apos;s what we
          delivered:
        </p>

        <div className="space-y-6">
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">1. Modern Festival Website (Next.js + Tailwind CSS)</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Professional visual design that reflects the festival&apos;s artistic prestige</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Built on Next.js for lightning-fast load times (under 2 seconds)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Mobile-first responsive design optimized for Gen Z applicants</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>SEO optimization for organic search visibility</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Core pages: Home, Programs, Faculty, Schedule, Apply, Contact, FAQ</span>
              </li>
            </ul>
          </div>

          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">2. Custom Application System with Payment Integration</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>3-step application flow: Personal Info &rarr; Program Selection &rarr; Payment</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Stripe payment integration (instant processing, no manual invoicing)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Save-and-resume functionality (applicants can return without losing progress)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>File upload for repertoire lists and recommendation letters</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Progress indicator showing exactly where applicants are in the process</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Automated confirmation emails sent immediately after submission</span>
              </li>
            </ul>
          </div>

          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">3. Custom Admin Panel for Application Management</h3>
            <p className="text-muted-foreground mb-3 text-sm">
              <strong>This was the game-changer.</strong> Instead of tracking applicants in spreadsheets or emails, the
              festival director now has a centralized dashboard to manage everything:
            </p>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Application Dashboard:</strong> View all applicants at a glance with status labels
                  (incomplete, submitted, accepted, enrolled)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Search &amp; Filter:</strong> Find applicants by name, instrument, age, location, or
                  application status
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Applicant Details:</strong> Click any applicant to view full application, uploaded files, and
                  payment status
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Status Updates:</strong> Mark applicants as accepted, waitlisted, or declined with one click
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Data Export:</strong> Export applicant data to CSV for faculty review or decision-making
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Real-time Updates:</strong> New applications appear instantly—no refreshing needed
                </span>
              </li>
            </ul>
          </div>

          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">4. Content Management System (Payload CMS)</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Easy-to-use CMS so festival staff can update content without a developer</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Faculty can update their own bios and headshots through a self-service portal</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Program details, schedules, and FAQs editable in real-time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>No developer needed for routine content updates</span>
              </li>
            </ul>
          </div>

          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-2 text-xl font-semibold">5. Automated Email Workflows (Mailchimp Integration)</h3>
            <ul className="text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Application Confirmation:</strong> Automated email sent immediately when student submits
                  application
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Incomplete Application Reminders:</strong> Automated reminders for students who started but
                  didn&apos;t finish
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>
                  <strong>Acceptance Emails:</strong> Triggered when festival director marks applicant as
                  &quot;accepted&quot;
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>All email sequences customizable through Mailchimp</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">Implementation Timeline</h2>
        <div className="space-y-4">
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="font-semibold">Week 1-2: Discovery &amp; Planning</h3>
            <p className="text-muted-foreground text-sm">
              Competitive analysis, content strategy, application workflow mapping, admin panel requirements, technical
              architecture planning
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="font-semibold">Week 3-5: Design &amp; Development</h3>
            <p className="text-muted-foreground text-sm">
              Visual design mockups, Next.js website development, CMS integration, application funnel build, Stripe
              payment setup
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="font-semibold">Week 6: Admin Panel Development</h3>
            <p className="text-muted-foreground text-sm">
              Custom admin dashboard, applicant management interface, search/filter functionality, data export features
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="font-semibold">Week 7: Testing &amp; Content Migration</h3>
            <p className="text-muted-foreground text-sm">
              QA testing (website + application flow + admin panel), content migration (faculty bios, program details),
              mobile optimization
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="font-semibold">Week 8: Launch &amp; Training</h3>
            <p className="text-muted-foreground text-sm">
              Staff training on CMS and admin panel, launch, email automation setup, post-launch monitoring
            </p>
          </div>
        </div>
      </section>

      {/* The Results */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">The Results</h2>
        <p className="text-muted-foreground leading-relaxed">
          Chroma launched successfully with a complete digital infrastructure that positioned them as a professional,
          modern festival from day one:
        </p>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-primary/5 border-primary/20 space-y-3 rounded-lg border p-6">
            <h3 className="text-xl font-semibold">Successful Launch</h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li>
                <strong className="text-foreground">Professional digital presence</strong> from day one (no &quot;under
                construction&quot; pages)
              </li>
              <li>
                <strong className="text-foreground">Seamless application process</strong> that reflected the
                festival&apos;s quality
              </li>
              <li>
                <strong className="text-foreground">Complete infrastructure</strong> in place before application season
                opened
              </li>
            </ul>
          </div>

          <div className="bg-primary/5 border-primary/20 space-y-3 rounded-lg border p-6">
            <h3 className="text-xl font-semibold">Operational Efficiency</h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li>
                <strong className="text-foreground">10+ hours/week saved</strong> on admin work (application tracking,
                content updates)
              </li>
              <li>
                <strong className="text-foreground">Zero manual payment processing</strong> (Stripe handles everything)
              </li>
              <li>
                <strong className="text-foreground">Automated email workflows</strong> eliminated manual follow-ups
              </li>
            </ul>
          </div>

          <div className="bg-primary/5 border-primary/20 space-y-3 rounded-lg border p-6">
            <h3 className="text-xl font-semibold">Application Management</h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li>
                <strong className="text-foreground">Centralized dashboard</strong> for all applicant data (no
                spreadsheet chaos)
              </li>
              <li>
                <strong className="text-foreground">Real-time application tracking</strong> with instant notifications
              </li>
              <li>
                <strong className="text-foreground">One-click status updates</strong> for acceptance decisions
              </li>
            </ul>
          </div>

          <div className="bg-primary/5 border-primary/20 space-y-3 rounded-lg border p-6">
            <h3 className="text-xl font-semibold">Modern User Experience</h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li>
                <strong className="text-foreground">Fast website load times</strong> (under 2 seconds)
              </li>
              <li>
                <strong className="text-foreground">Mobile-optimized design</strong> for Gen Z applicants
              </li>
              <li>
                <strong className="text-foreground">Streamlined 3-step application</strong> with progress tracking
              </li>
            </ul>
          </div>
        </div>

        <blockquote className="border-primary text-muted-foreground bg-primary/5 rounded-r border-l-4 py-4 pl-6 text-lg italic">
          &quot;The admin panel changed everything. I can see every applicant, their status, and all their materials in
          one place. No more digging through emails or spreadsheets. It saves me hours every week and makes the whole
          process feel professional and organized.&quot;
          <footer className="mt-2 text-sm not-italic">— Festival Director, Chroma International Music Festival</footer>
        </blockquote>
      </section>

      {/* Admin Panel Deep Dive */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">Admin Panel: A Closer Look</h2>
        <p className="text-muted-foreground leading-relaxed">
          The custom admin panel was the most valuable part of the system. Here&apos;s why it made such a difference:
        </p>

        <div className="space-y-4">
          <div className="border-border rounded-lg border p-6">
            <h3 className="mb-3 text-xl font-semibold">Before: Spreadsheet Chaos</h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-500">&#10007;</span>
                <span>Applications arriving via email, Google Forms, or direct messages</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-500">&#10007;</span>
                <span>Manual data entry into spreadsheets</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-500">&#10007;</span>
                <span>Files scattered across email attachments and Google Drive</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 text-red-500">&#10007;</span>
                <span>No way to quickly answer: &quot;How many violinists applied?&quot;</span>
              </li>
            </ul>
          </div>

          <div className="border-primary bg-primary/5 rounded-lg border p-6">
            <h3 className="mb-3 text-xl font-semibold">After: Centralized Admin Panel</h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>All applications in one central dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Automatic data capture (no manual entry)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>All files attached to applicant profiles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">&#10003;</span>
                <span>Instant answers via filters: &quot;Show me all violinists aged 16-18&quot;</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">Technical Stack</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="border-border rounded-lg border p-4">
            <h3 className="mb-2 font-semibold">Frontend</h3>
            <ul className="text-muted-foreground space-y-1 text-sm">
              <li>&bull; Next.js 14 (React framework)</li>
              <li>&bull; Tailwind CSS (styling)</li>
              <li>&bull; TypeScript (type safety)</li>
              <li>&bull; Vercel (hosting &amp; deployment)</li>
            </ul>
          </div>
          <div className="border-border rounded-lg border p-4">
            <h3 className="mb-2 font-semibold">Backend &amp; CMS</h3>
            <ul className="text-muted-foreground space-y-1 text-sm">
              <li>&bull; Payload CMS (content management)</li>
              <li>&bull; PostgreSQL (database)</li>
              <li>&bull; Stripe (payment processing)</li>
            </ul>
          </div>
          <div className="border-border rounded-lg border p-4">
            <h3 className="mb-2 font-semibold">Application System</h3>
            <ul className="text-muted-foreground space-y-1 text-sm">
              <li>&bull; Custom React forms with validation</li>
              <li>&bull; File upload with cloud storage</li>
              <li>&bull; Stripe Checkout integration</li>
              <li>&bull; Save-and-resume functionality</li>
            </ul>
          </div>
          <div className="border-border rounded-lg border p-4">
            <h3 className="mb-2 font-semibold">Admin Panel</h3>
            <ul className="text-muted-foreground space-y-1 text-sm">
              <li>&bull; Custom dashboard built in Payload</li>
              <li>&bull; Real-time data updates</li>
              <li>&bull; Advanced search &amp; filtering</li>
              <li>&bull; CSV export functionality</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="article-section space-y-6">
        <h2 className="text-3xl font-bold">Key Takeaways for New Festivals</h2>
        <div className="space-y-4">
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-1 font-semibold">1. Build everything together from the start</h3>
            <p className="text-muted-foreground text-sm">
              Don&apos;t cobble together five different tools (website, Google Forms, spreadsheets, PayPal). One
              integrated system saves time and prevents data chaos.
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-1 font-semibold">2. Invest in an admin panel early</h3>
            <p className="text-muted-foreground text-sm">
              The admin panel saves 10+ hours/week and makes your festival look professional. It&apos;s not a
              luxury—it&apos;s essential for efficient operations.
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-1 font-semibold">3. Automate everything you can</h3>
            <p className="text-muted-foreground text-sm">
              Automated emails, payment processing, and application confirmations eliminate hours of manual work. Focus
              your time on programming and faculty—not admin tasks.
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-1 font-semibold">4. Mobile optimization is non-negotiable</h3>
            <p className="text-muted-foreground text-sm">
              Gen Z applicants browse on phones. If your site doesn&apos;t work on mobile, you&apos;ll lose applicants
              before they even read about your programs.
            </p>
          </div>
          <div className="border-primary border-l-4 py-2 pl-6">
            <h3 className="mb-1 font-semibold">5. Plan for 8-10 weeks of development</h3>
            <p className="text-muted-foreground text-sm">
              Building a complete digital infrastructure (website + application system + admin panel) takes time. Start
              early so everything&apos;s ready before application season opens.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-12 text-center">
        <h2 className="text-3xl font-bold">Launching a New Festival? Let&apos;s Build Your Digital Infrastructure</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Schedule a free 15-minute strategy call to discuss your festival&apos;s needs. We&apos;ll walk through what
          you need (website, application system, admin panel) and show you exactly how we can help—just like we did for
          Chroma.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Schedule Your Free Strategy Call &rarr;
              </Button>
            </a>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/packages">
              <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                View Packages &amp; Pricing
              </Button>
            </Link>
          </motion.div>
        </div>
        <p className="text-muted-foreground text-sm">
          No sales pitch. Just honest feedback and a clear roadmap if you want our help.
        </p>
      </section>

      {/* Back to Blog */}
      <div className="pt-8 text-center">
        <Link to="/blog" className="text-primary hover:underline">
          &larr; Back to all articles
        </Link>
      </div>
    </article>
  )
}
